import React, { useState, useMemo } from 'react';

// ── NSE Trading Holidays 2026 ─────────────────────────────────────────────────
// Source: NSE Circular Ref. No. 172/2025 dated Dec 12, 2025
// 15 confirmed weekday trading holidays. Holidays falling on weekends are excluded.
// Note: Independence Day (Aug 15 Sat), Mahashivratri (Feb 15 Sun),
//       Id-ul-Fitr (Mar 21 Sat) and Diwali Laxmi Puja (Nov 8 Sun) fall on
//       weekends — no market closure; Muhurat Trading held on Nov 8 evening.
const NSE_HOLIDAYS = [
  { date: '2026-01-26', name: 'Republic Day',         type: 'holiday' },
  { date: '2026-03-03', name: 'Holi',                 type: 'holiday' },
  { date: '2026-03-26', name: 'Shri Ram Navami',      type: 'holiday' },
  { date: '2026-03-31', name: 'Shri Mahavir Jayanti', type: 'holiday' },
  { date: '2026-04-03', name: 'Good Friday',          type: 'holiday' },
  { date: '2026-04-14', name: 'Dr. Ambedkar Jayanti', type: 'holiday' },
  { date: '2026-05-01', name: 'Maharashtra Day',       type: 'holiday' },
  { date: '2026-05-28', name: 'Id-Ul-Adha (Bakri Eid)', type: 'holiday' },
  { date: '2026-06-26', name: 'Muharram',              type: 'holiday' },
  { date: '2026-09-14', name: 'Ganesh Chaturthi',     type: 'holiday' },
  { date: '2026-10-02', name: 'Gandhi Jayanti',        type: 'holiday' },
  { date: '2026-10-20', name: 'Dussehra',              type: 'holiday' },
  { date: '2026-11-10', name: 'Diwali Balipratipada', type: 'holiday' },
  { date: '2026-11-24', name: 'Guru Nanak Jayanti',   type: 'holiday' },
  { date: '2026-12-25', name: 'Christmas',             type: 'holiday' },
  // Special: not a closure but notable
  { date: '2026-11-08', name: 'Muhurat Trading 🪔',   type: 'muhurat' },
];

// ── Upcoming Market Events ────────────────────────────────────────────────────
const MARKET_EVENTS = [
  // RBI MPC 2026
  { date: '2026-04-09', name: 'RBI MPC Result',       type: 'rbi',      icon: '🏦' },
  { date: '2026-06-05', name: 'RBI MPC Result',       type: 'rbi',      icon: '🏦' },
  { date: '2026-08-07', name: 'RBI MPC Result',       type: 'rbi',      icon: '🏦' },
  { date: '2026-10-09', name: 'RBI MPC Result',       type: 'rbi',      icon: '🏦' },
  { date: '2026-12-04', name: 'RBI MPC Result',       type: 'rbi',      icon: '🏦' },
  // Earnings seasons
  { date: '2026-04-15', name: 'Q4 Results Season',    type: 'earnings', icon: '📊' },
  { date: '2026-07-15', name: 'Q1 Results Season',    type: 'earnings', icon: '📊' },
  { date: '2026-10-15', name: 'Q2 Results Season',    type: 'earnings', icon: '📊' },
  { date: '2027-01-15', name: 'Q3 Results Season',    type: 'earnings', icon: '📊' },
  // F&O Expiry (last Thursday of month — approximate)
  { date: '2026-04-23', name: 'Monthly F&O Expiry',   type: 'expiry',   icon: '⏱' },
  { date: '2026-05-28', name: 'Monthly F&O Expiry',   type: 'expiry',   icon: '⏱' },
  { date: '2026-06-25', name: 'Monthly F&O Expiry',   type: 'expiry',   icon: '⏱' },
  { date: '2026-07-30', name: 'Monthly F&O Expiry',   type: 'expiry',   icon: '⏱' },
  { date: '2026-08-27', name: 'Monthly F&O Expiry',   type: 'expiry',   icon: '⏱' },
  { date: '2026-09-24', name: 'Monthly F&O Expiry',   type: 'expiry',   icon: '⏱' },
  { date: '2026-10-29', name: 'Monthly F&O Expiry',   type: 'expiry',   icon: '⏱' },
  { date: '2026-11-26', name: 'Monthly F&O Expiry',   type: 'expiry',   icon: '⏱' },
  { date: '2026-12-31', name: 'Monthly F&O Expiry',   type: 'expiry',   icon: '⏱' },
];

const EVENT_COLORS = {
  holiday:  { bg: '#dc262611', border: '#dc262633', color: '#ef4444',   label: 'Holiday' },
  muhurat:  { bg: '#f9731611', border: '#f9731633', color: '#f97316',   label: 'Muhurat Trading' },
  rbi:      { bg: '#3b82f611', border: '#3b82f633', color: '#3b82f6',   label: 'RBI MPC' },
  earnings: { bg: '#eab30811', border: '#eab30833', color: '#eab308',   label: 'Earnings' },
  expiry:   { bg: '#6366f111', border: '#6366f133', color: '#6366f1',   label: 'F&O Expiry' },
};

function daysFrom(dateStr) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  return Math.round((target - today) / 86400000);
}

function fmtDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', weekday: 'short',
  });
}

function DaysChip({ days }) {
  if (days < 0) return null;
  if (days === 0) return <span style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', background: '#dc262618', border: '1px solid #dc262633', borderRadius: 99, padding: '1px 7px' }}>TODAY</span>;
  if (days === 1) return <span style={{ fontSize: 10, fontWeight: 700, color: '#eab308', background: '#eab30818', border: '1px solid #eab30833', borderRadius: 99, padding: '1px 7px' }}>TOMORROW</span>;
  return <span style={{ fontSize: 10, color: 'var(--muted)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 99, padding: '1px 7px' }}>{days}d</span>;
}

export default function MarketCalendar() {
  const [tab, setTab] = useState('upcoming');

  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  // Merge holidays + events, filter future only, sort by date
  const upcomingAll = useMemo(() => {
    const all = [
      ...NSE_HOLIDAYS.map(h => ({ ...h })),
      ...MARKET_EVENTS.map(e => ({ ...e })),
    ]
      .map(x => ({ ...x, days: daysFrom(x.date) }))
      .filter(x => x.days >= 0)
      .sort((a, b) => a.days - b.days);
    return all;
  }, [today]);

  // Next holiday specifically
  const nextHoliday = useMemo(() => NSE_HOLIDAYS.map(h => ({ ...h, days: daysFrom(h.date) })).filter(h => h.days >= 0).sort((a,b) => a.days - b.days)[0], [today]);

  // All holidays with past/future tag
  const allHolidays = useMemo(() =>
    NSE_HOLIDAYS.map(h => ({ ...h, days: daysFrom(h.date) })).sort((a,b) => a.days - b.days), [today]);

  return (
    <div className="card">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>📅 Market Calendar</div>
        <div style={{ fontSize: 10, color: 'var(--muted)', fontStyle: 'italic' }}>NSE Circular 172/2025</div>
      </div>

      {/* Next holiday banner */}
      {nextHoliday && (
        <div style={{
          background: '#dc262611', border: '1px solid #dc262633', borderRadius: 8,
          padding: '8px 12px', marginBottom: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
              🎌 Next Holiday
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{nextHoliday.name}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{fmtDate(nextHoliday.date)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <DaysChip days={nextHoliday.days} />
            {nextHoliday.days > 1 && (
              <div style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', marginTop: 4 }}>
                {nextHoliday.days} days
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 10 }}>
        {[['upcoming', 'Upcoming'], ['holidays', 'All Holidays']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '5px 12px', fontSize: 12, fontWeight: 600, marginBottom: -1,
            color:        tab === key ? 'var(--accent)' : 'var(--muted)',
            borderBottom: tab === key ? '2px solid var(--accent)' : '2px solid transparent',
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* Upcoming tab — mixed events */}
      {tab === 'upcoming' && (
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          {upcomingAll.slice(0, 12).map((ev, i) => {
            const meta = EVENT_COLORS[ev.type] || EVENT_COLORS.holiday;
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '6px 0', borderBottom: '1px solid var(--border)',
              }}>
                {/* Date block */}
                <div style={{ minWidth: 38, textAlign: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: meta.color, lineHeight: 1 }}>
                    {new Date(ev.date).getDate()}
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--muted)', textTransform: 'uppercase' }}>
                    {new Date(ev.date).toLocaleString('en-IN', { month: 'short' })}
                  </div>
                </div>
                {/* Event name */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 5 }}>
                    {ev.icon || '🎌'} {ev.name}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                    {new Date(ev.date).toLocaleString('en-IN', { weekday: 'short' })} · {meta.label}
                  </div>
                </div>
                <DaysChip days={ev.days} />
              </div>
            );
          })}
          {upcomingAll.length === 0 && (
            <div style={{ color: 'var(--muted)', fontSize: 12, padding: '12px 0' }}>No upcoming events found.</div>
          )}
        </div>
      )}

      {/* All Holidays tab */}
      {tab === 'holidays' && (
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          {allHolidays.map((h, i) => {
            const past = h.days < 0;
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '6px 0', borderBottom: '1px solid var(--border)',
                opacity: past ? 0.4 : 1,
              }}>
                <div style={{ minWidth: 38, textAlign: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: past ? 'var(--muted)' : '#ef4444', lineHeight: 1 }}>
                    {new Date(h.date).getDate()}
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--muted)', textTransform: 'uppercase' }}>
                    {new Date(h.date).toLocaleString('en-IN', { month: 'short' })}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: past ? 'var(--muted)' : 'var(--text)' }}>
                    🎌 {h.name}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                    {new Date(h.date).toLocaleString('en-IN', { weekday: 'long' })}
                  </div>
                </div>
                {!past && <DaysChip days={h.days} />}
                {past && <span style={{ fontSize: 10, color: 'var(--muted)' }}>done</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
        {Object.entries(EVENT_COLORS).map(([key, meta]) => (
          <span key={key} style={{ fontSize: 10, color: meta.color, display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color, display: 'inline-block' }} />
            {meta.label}
          </span>
        ))}
      </div>
    </div>
  );
}
